import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "~/components/ui";
import { useReferenceFileAdd } from "~/features/references/hooks";
import { type ReferenceFileAddFormData, zReferenceFileAddFormData } from "~/features/references/types";

const FORM_ID = "reference-file-add-form";

interface ReferenceFileAddDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  referenceId: string;
  /** True when replacing an existing file (vs adding the first). */
  replace?: boolean;
}

export function ReferenceFileAddDialog({ open, onOpenChange, referenceId, replace }: ReferenceFileAddDialogProps) {
  const { t } = useTranslation();
  const form = useForm<ReferenceFileAddFormData>({
    resolver: zodResolver(zReferenceFileAddFormData),
    defaultValues: { googleDriveFileId: "" },
  });
  const { submit, processing } = useReferenceFileAdd({
    referenceId,
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">
            {`${replace ? t("ui.replace", { defaultValue: "Replace" }) : t("ui.add")} ${t("model.referenceFile.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form id={FORM_ID} onSubmit={form.handleSubmit(submit)}>
            <FormField
              control={form.control}
              name="googleDriveFileId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.referenceFile.googleDriveFileId")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription>{t("feature.references.file.googleDriveFileIdHelp")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={processing}>
            {t("ui.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
